// STEP STUDIO-OVERLAY-COMBINED-PRESET-1 · 통합 카드 (판매자 미리보기 전용 · 소비자/Egress 미반영).
//   COMBINED-CARD-DESIGN-FIX-1: 대표 디자인 목업 기준 컴팩트 카드 — 상품 사진 우선 · 사이즈표/QR 은 작은 보조 영역.
//   · 가로형: 사진 | 번호·이름·가격·옵션 (+작은 사이즈표) | 사이즈 요약 + QR
//   · 세로형: 사진 / 번호·이름·가격·옵션 / 회색 보조칸(작은 사이즈표 + QR)
//   · 미니: 작은 사진 · 번호 · 가격 (+QR) — 상품명 생략(기존 정책) · 사이즈표 미표시
//   · 사이즈표·QR 미선택/데이터 없음 → 해당 영역 제거 후 자동 재배치.
import React from 'react';
import SafeImg from '@/components/SafeImg';
import { formatWon, optionGroups } from '@/components/live/LivePresetOverlay';
import { QrImage, sizeTableDesc } from '@/components/live/OverlayExtras';

const clamp = (v, a, b) => Math.round(Math.min(b, Math.max(a, v)));
const RED = '#FF3B30';

const Photo = ({ product, size, height }) => (
  <div style={{ width: size, height: height || size, flexShrink: 0, borderRadius: 10, overflow: 'hidden', background: '#F2F2F2', position: 'relative' }}
       data-testid="combined-card-photo">
    {product?.image && <SafeImg src={product.image} kind="product" alt="" className="absolute inset-0 w-full h-full object-contain" />}
  </div>
);

const Qr = ({ url, size, sample }) => (
  <div style={{ position: 'relative', flexShrink: 0, background: '#fff', borderRadius: 8, padding: 3, lineHeight: 0 }} data-testid="combined-card-qr">
    <QrImage url={url} size={size} />
    {sample && (
      <span style={{ position: 'absolute', left: '50%', bottom: -7, transform: 'translateX(-50%)', padding: '0 5px', borderRadius: 999,
                     background: '#FFB020', color: '#111', fontSize: 9, fontWeight: 900, lineHeight: '14px', whiteSpace: 'nowrap' }}
            data-testid="combined-card-qr-sample">샘플</span>
    )}
  </div>
);

// 작은 사이즈표 — 최대 rowsMax행 · 3열 · 최소 9px (가독 하한) · compact = 헤더 생략(좁은 폭).
const MiniTable = ({ sizeTable, font, rowsMax = 4, compact = false }) => {
  const allCols = sizeTable?.columns || [];
  const allRows = sizeTable?.rows || [];
  const cols = allCols.slice(0, 3);
  const rows = allRows.slice(0, rowsMax);
  const hiddenRows = Math.max(0, allRows.length - rows.length);
  const hiddenCols = Math.max(0, allCols.length - cols.length);
  const desc = sizeTableDesc(sizeTable);
  if (!cols.length && !rows.length && !desc.length) return null;
  const cell = { padding: '1px 3px', whiteSpace: 'nowrap', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' };
  return (
    <div data-testid="combined-card-desc">
    {(cols.length > 0 || rows.length > 0) && <table style={{ borderCollapse: 'collapse', fontSize: font, lineHeight: 1.25, color: '#333', width: '100%', tableLayout: 'fixed' }}
           data-testid="combined-card-size-table">
      {cols.length > 0 && !compact && (
        <thead><tr>{cols.map((c, i) => <th key={i} style={{ ...cell, fontWeight: 900, color: '#777' }}>{c}</th>)}</tr></thead>
      )}
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>{(Array.isArray(r) ? r : []).slice(0, 3).map((v, j) => <td key={j} style={{ ...cell, fontWeight: j === 0 ? 900 : 600 }}>{v}</td>)}</tr>
        ))}
        {(hiddenRows > 0 || hiddenCols > 0) && (
          <tr><td colSpan={Math.max(1, cols.length)} style={{ ...cell, color: '#888', fontWeight: 800 }} data-testid="combined-card-size-more">
            {[hiddenRows > 0 ? `외 ${hiddenRows}개` : '', hiddenCols > 0 ? `${hiddenCols}열 더` : ''].filter(Boolean).join(' · ')}
          </td></tr>
        )}
      </tbody>
    </table>}
    {desc.map(([k, v]) => (
      <div key={k} style={{ display: 'flex', gap: 4, fontSize: font, lineHeight: 1.25, color: '#333', padding: '1px 3px', minWidth: 0 }}
           data-testid={`combined-card-desc-${k === '소재' ? 'material' : 'etc'}`}>
        <span style={{ fontWeight: 900, color: '#777', flexShrink: 0 }}>{k}</span>
        <span style={{ fontWeight: 700, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: compact ? 1 : 2, WebkitBoxOrient: 'vertical', wordBreak: 'keep-all' }}>{v}</span>
      </div>
    ))}
    </div>
  );
};

const Info = ({ product, mini, nameFont, priceFont, showOptions = true }) => {
  const opt = optionGroups(product || {})[0];
  const slot = product?.slot_number ? `${product.slot_number}번` : '';
  // 상품명에 이미 'N번 · ' 이 붙어 있으면 중복 제거.
  const name = (product?.name || '').replace(new RegExp(`^\\s*${product?.slot_number || 'x'}번\\s*[·.\\-]?\\s*`), '');
  return (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="combined-card-info">
      <div style={{ fontSize: nameFont, fontWeight: 900, color: '#111', lineHeight: 1.25, overflow: 'hidden',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'keep-all' }}
           data-testid="combined-card-name">
        {slot}{!mini && name ? `${slot ? ' · ' : ''}${name}` : ''}
      </div>
      <div style={{ fontSize: priceFont, fontWeight: 900, color: RED, lineHeight: 1.15 }} data-testid="combined-card-price">
        {formatWon(product?.sale_price ?? product?.price)}
      </div>
      {showOptions && opt?.values?.length > 0 && (
        <div style={{ fontSize: Math.max(9, nameFont - 2), fontWeight: 700, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
             data-testid="combined-card-options">{opt.values.join(' / ')}</div>
      )}
    </div>
  );
};

const shell = (pad) => ({ position: 'relative', width: '100%', padding: pad, borderRadius: 16, background: 'rgba(255,255,255,0.97)',
  boxShadow: '0 8px 24px rgba(0,0,0,0.28)', overflow: 'hidden', boxSizing: 'border-box' });

const Horizontal = ({ product, mini, sizeTable, qrUrl, sampleQr, width }) => {
  let photo = clamp(width * (mini ? 0.24 : 0.26), mini ? 44 : 56, mini ? 76 : 110);
  const qr = qrUrl ? clamp(width * (mini ? 0.19 : 0.17), 52, Math.min(84, photo)) : 0;
  // 정보 영역이 90px 미만이면 QR 대신 사진을 먼저 줄임 (QR 최소 52px 유지).
  const info = width - photo - qr - 16 - (qr ? 20 : 10);
  if (info < 90) photo = Math.max(44, photo - (90 - info));
  const nameFont = clamp(width / 26, 11, 16);
  return (
    <div style={{ ...shell(8), display: 'flex', alignItems: 'center', gap: 10 }} data-testid="combined-card-horizontal">
      <Photo product={product} size={photo} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Info product={product} mini={mini} nameFont={nameFont} priceFont={clamp(width / (mini ? 16 : 20), 13, 22)} />
        {!mini && sizeTable && (
          <div style={{ background: '#F4F4F5', borderRadius: 8, padding: '2px 4px' }}><MiniTable sizeTable={sizeTable} font={clamp(width / 38, 9, 11)} rowsMax={width >= 330 ? 4 : 3} /></div>
        )}
      </div>
      {qr > 0 && <Qr url={qrUrl} size={qr} sample={sampleQr} />}
    </div>
  );
};

const Vertical = ({ product, mini, sizeTable, qrUrl, sampleQr, width }) => {
  const inner = width - 14;
  const qr = qrUrl ? clamp(inner * (mini ? 0.42 : 0.36), 52, 76) : 0;
  const st = !mini && sizeTable;
  return (
    <div style={{ ...shell(7), display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="combined-card-vertical">
      <Photo product={product} size="100%" height={Math.round(inner * (mini ? 0.8 : 0.92))} />
      {mini ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}><Info product={product} mini nameFont={clamp(inner / 11, 10, 14)} priceFont={clamp(inner / 8, 12, 18)} showOptions={false} /></div>
          {qr > 0 && <Qr url={qrUrl} size={qr} sample={sampleQr} />}
        </div>
      ) : (
        <>
          <Info product={product} nameFont={clamp(inner / 11, 11, 15)} priceFont={clamp(inner / 8, 13, 19)} />
          {(st || qr > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F4F4F5', borderRadius: 10, padding: 5 }}
                 data-testid="combined-card-extras">
              {st && <div style={{ flex: 1, minWidth: 0 }}><MiniTable sizeTable={sizeTable} font={clamp(inner / 15, 9, 11)} rowsMax={inner >= 180 ? 5 : 4} compact={qr > 0 && inner < 200} /></div>}
              {qr > 0 && <Qr url={qrUrl} size={st ? Math.min(qr, Math.round(inner * 0.4)) : qr} sample={sampleQr} />}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const OverlayCombinedCard = ({ product, preset, orientation, sizeTable, qrUrl, qrLabel, width, testid = 'studio-overlay-combined-card' }) => {
  const mini = preset === 'mini';
  const props = { product, mini, sizeTable: mini ? null : sizeTable, qrUrl, sampleQr: /샘플/.test(qrLabel || ''), width };
  return (
    <div data-testid={testid} data-orientation={orientation} data-mini={mini ? '1' : '0'}
         data-items={`${props.sizeTable ? 'st' : ''}${qrUrl ? 'qr' : ''}`}>
      {orientation === 'vertical' ? <Vertical {...props} /> : <Horizontal {...props} />}
    </div>
  );
};

export default OverlayCombinedCard;
